import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Input, Select, Row, Space } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  clearCustomers,
  clearProductIds,
  clearStylesForJobchart,
  getCustomers,
  getProductIds,
  getStylesForJobchart,
  getColorIds,
  getFabricStocks,
  clearColorIds,
  clearFabricStocks,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import ProductForm from './ProductForm';

const AntTextArea = GetField(Input.TextArea);
const AntSelect = GetField(Select);
const { Option } = Select;

const CreateSample = props => {
  const { classes } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const [confirmLoading, setConfirmLoading] = useState(false);

  const filesState = useSelector(state => state.sampleFiles);

  const { handleSubmit, control, getValues, setValue, watch, unregister } =
    useForm({
      defaultValues: {
        products: [
          {
            productId: null,
            isAll: false,
            styles: [
              {
                fabrics: [{ fabricId: null }],
                accessories: [{ accessoryId: null }],
              },
            ],
          },
        ],
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  const customers = useSelector(state => state.customers);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getColorIds());
    dispatch(getFabricStocks());
    return () => {
      dispatch(clearCustomers());
      dispatch(clearProductIds());
      dispatch(clearColorIds());
      dispatch(clearFabricStocks());
    };
  }, []);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      const formData = new FormData();

      const { productIndexes } = filesState;

      formData.append(
        'data',
        JSON.stringify({
          ...formValues,
          productIndexes,
        })
      );

      let index = 0;

      for (const key of Object.keys(filesState.products)) {
        if (filesState.products[key] && filesState.products[key].files.length) {
          for (const file of filesState.products[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      await Api.post('/samples', formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      successNotification('Sample created');
      history.push('/samples/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  const productAppend = useCallback(() => {
    append({ isAll: false, styles: [{ fabrics: [{}], accessories: [{}] }] });
  }, [append]);

  return (
    <>
      <Breadcrumbs
        moduleName="Sample"
        moduleLink="/samples/list"
        subModuleName="Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                  onChange={e => {
                    field.onChange(e);

                    if (!e) {
                      dispatch(clearStylesForJobchart());
                      dispatch(clearProductIds());
                    } else {
                      dispatch(getProductIds(e));
                      dispatch(getStylesForJobchart(e));
                    }
                  }}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row style={{ padding: 30 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {fields.map((item, index) => (
              <ProductForm
                key={item.id}
                productItem={item}
                productIndex={index}
                productRemove={remove}
                control={control}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                unregister={unregister}
              />
            ))}
          </Space>
        </Row>

        <Row className={classes.buttonContainer}>
          <Button type="primary" htmlType="button" onClick={productAppend}>
            <PlusOutlined /> Product
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
    </>
  );
};

CreateSample.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateSample);
